import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Delete,
  Campaign,
  Add,
  Image as ImageIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import AdvertisementFormDialog from '../components/AdvertisementFormDialog';
import { isOwnerRole } from '../utils/roles';

interface AdvertisementItem {
  _id: string;
  type: 'advertisement' | 'banner';
  title?: string;
  buttonTitle?: string;
  description?: string;
  footerMessage?: string;
  image: string;
  link?: string;
  studio?: string | {
    _id: string;
    studioName: string;
  };
  event?: string | {
    _id: string;
    eventName: string;
  };
  session?: string | {
    _id: string;
    className: string;
  };
  isLive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdvertisementsPage: React.FC = () => {
  const { admin } = useAuth();
  const isStudioMode = isOwnerRole(admin?.role) || admin?.role === 'manager';
  const studioId = typeof admin?.studio === 'string' ? admin.studio : admin?.studio?._id;
  const { isAllLocations, byId: locationById } = useMyLocations();

  const [advertisements, setAdvertisements] = useState<AdvertisementItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'advertisement' | 'banner'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'inactive'>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAd, setSelectedAd] = useState<AdvertisementItem | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchAdvertisements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  const fetchAdvertisements = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: any = {};

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      if (statusFilter === 'live') {
        params.isLive = true;
      } else if (statusFilter === 'inactive') {
        params.isLive = false;
      }

      const response = isStudioMode
        ? await apiService.getStudioAdvertisements(params)
        : await apiService.getAdvertisements(params);
      setAdvertisements(response.advertisements || []);
    } catch (err: any) {
      console.error('Error fetching advertisements:', err);
      setError(err.message || 'Failed to fetch advertisements');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, ad: AdvertisementItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedAd(ad);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAd(null);
  };

  const handleEdit = () => {
    if (selectedAd) {
      setFormDialogOpen(true);
    }
    // Close the menu but DON'T clear selectedAd yet - dialog needs it
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    // Close the menu but DON'T clear selectedAd yet - dialog needs it
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedAd) return;

    try {
      setDeleting(true);
      if (isStudioMode) {
        // Pinned to the ad's own location — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
        await apiService.deleteStudioAdvertisement(selectedAd._id, studioIdOf(selectedAd.studio));
      } else {
        await apiService.deleteAdvertisement(selectedAd._id);
      }
      await fetchAdvertisements();
      setDeleteDialogOpen(false);
      setSelectedAd(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete advertisement');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (ad: AdvertisementItem) => {
    try {
      setToggling(true);
      if (isStudioMode) {
        await apiService.toggleStudioAdvertisementStatus(ad._id, !ad.isLive, studioIdOf(ad.studio));
      } else {
        await apiService.toggleAdvertisementStatus(ad._id, !ad.isLive);
      }
      await fetchAdvertisements();
    } catch (err: any) {
      setError(err.message || 'Failed to update advertisement status');
    } finally {
      setToggling(false);
    }
  };

  const handleFormSuccess = () => {
    fetchAdvertisements();
    setFormDialogOpen(false);
  };

  const filteredAds = advertisements.filter(ad => {
    const matchesSearch = !searchTerm || 
      (ad.title?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (ad.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    return matchesSearch;
  });

  const getTypeColor = (type: string) => {
    return type === 'banner' ? colors.primary : colors.secondary;
  };

  const getTypeLabel = (type: string) => {
    return type === 'banner' ? 'Banner' : 'Advertisement';
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700} mb={1}>
            Advertisements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage advertisements and banners
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedAd(null);
            setFormDialogOpen(true);
          }}
          sx={{ backgroundColor: colors.primary }}
        >
          Create Advertisement
        </Button>
      </Box>

      {/* Filters */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <TextField
          variant="outlined"
          placeholder="Search advertisements..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />
        
        {/* A studio owner has only advertisements — banners are a SweatSocial
            app placement — so a type filter would be a row of controls that
            can only ever return the same list or an empty one. */}
        {!isStudioMode && (
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, value) => value && setTypeFilter(value)}
            size="small"
          >
            <ToggleButton value="all">All Types</ToggleButton>
            <ToggleButton value="advertisement">Advertisements</ToggleButton>
            <ToggleButton value="banner">Banners</ToggleButton>
          </ToggleButtonGroup>
        )}

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, value) => value && setStatusFilter(value)}
          size="small"
        >
          <ToggleButton value="all">All Status</ToggleButton>
          <ToggleButton value="live">Live</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredAds.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Campaign sx={{ fontSize: 64, color: colors.greyText3, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" mb={1}>
                No advertisements found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first advertisement to get started'}
              </Typography>
              {!searchTerm && typeFilter === 'all' && statusFilter === 'all' && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setFormDialogOpen(true)}
                  sx={{ backgroundColor: colors.primary }}
                >
                  Create Advertisement
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
          {filteredAds.map((ad) => (
            <Card
              key={ad._id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                border: `1px solid ${colors.divider}`,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <Box position="relative">
                {ad.image ? (
                  <Box
                    component="img"
                    src={ad.image}
                    alt={ad.title || 'Advertisement'}
                    sx={{
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      backgroundColor: colors.backgroundTertiary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4,
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 32, color: colors.greyText3 }} />
                  </Box>
                )}
                <Box
                  position="absolute"
                  top={6}
                  right={6}
                  display="flex"
                  gap={0.5}
                >
                  <Chip
                    label={getTypeLabel(ad.type)}
                    size="small"
                    sx={{
                      backgroundColor: getTypeColor(ad.type),
                      color: 'white',
                      fontWeight: 600,
                      height: 22,
                      fontSize: '0.6875rem',
                    }}
                  />
                  <Chip
                    label={ad.isLive ? 'Live' : 'Inactive'}
                    size="small"
                    sx={{
                      backgroundColor: ad.isLive ? colors.success : colors.greyText3,
                      color: 'white',
                      fontWeight: 600,
                      height: 22,
                      fontSize: '0.6875rem',
                    }}
                  />
                </Box>
              </Box>

              <CardContent
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: '12px',
                  '&:last-child': { pb: '12px' },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ flex: 1, fontSize: '0.9375rem', lineHeight: 1.3 }}
                  >
                    {ad.title || 'Untitled Advertisement'}
                  </Typography>
                  {/* Which location's app shows it — only under "All locations". */}
                  {isAllLocations && <Box sx={{ mr: 0.5 }}><BranchChip location={locationById(studioIdOf(ad.studio))} /></Box>}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, ad)}
                    sx={{ mt: -0.5, mr: -0.5 }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>

                {ad.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      fontSize: '0.8125rem',
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {ad.description}
                  </Typography>
                )}

                {ad.buttonTitle && (
                  <Chip
                    label={ad.buttonTitle}
                    size="small"
                    sx={{ mb: 0.75, alignSelf: 'flex-start', height: 22, fontSize: '0.6875rem' }}
                  />
                )}

                <Box mt="auto" pt={1} display="flex" justifyContent="space-between" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ad.isLive}
                        onChange={() => handleToggleStatus(ad)}
                        disabled={toggling}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.6875rem' }}>
                        {ad.isLive ? 'Live' : 'Inactive'}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    {new Date(ad.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Form Dialog */}
      <AdvertisementFormDialog
        key={selectedAd?._id || 'create'} // Force re-render when advertisement changes
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedAd(null);
        }}
        onSuccess={handleFormSuccess}
        isStudioMode={isStudioMode}
        defaultStudioId={isStudioMode ? studioId : undefined}
        advertisement={selectedAd ? {
          _id: selectedAd._id,
          type: selectedAd.type,
          title: selectedAd.title || '',
          buttonTitle: selectedAd.buttonTitle || '',
          description: selectedAd.description || '',
          footerMessage: selectedAd.footerMessage || '',
          image: selectedAd.image || '',
          link: selectedAd.link || '',
          // Handle both string ID and object with _id
          studio: typeof selectedAd.studio === 'string' 
            ? selectedAd.studio 
            : selectedAd.studio?._id || '',
          event: typeof selectedAd.event === 'string' 
            ? selectedAd.event 
            : selectedAd.event?._id || '',
          session: typeof selectedAd.session === 'string' 
            ? selectedAd.session 
            : selectedAd.session?._id || '',
          isLive: selectedAd.isLive !== undefined ? selectedAd.isLive : true,
        } : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this advertisement?
          </Typography>
          <Typography color="error" mt={1}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvertisementsPage;

